import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip } from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Palette as PaletteIcon,
  Store as StoreIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { SupportLayout, VisualExample } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const ThemesSupportPage: React.FC = () => {
  useDocumentTitle('Themes - Support');

  return (
    <SupportLayout
      title="Board Themes"
      description="Customize the look and feel of your boards with themes. Browse the theme store to find free and premium themes for your pools."
      breadcrumbs={[{ label: 'Themes' }]}
    >
      {/* Overview */}
      <Box id="theme-overview" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <PaletteIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          What Are Themes?
        </Typography>

        <Typography variant="body1" paragraph>
          Themes control the visual appearance of your board—colors, gradients, fonts, and styling. A well-chosen theme makes your pool more engaging and memorable for participants.
        </Typography>

        <Paper elevation={0} sx={{ p: 4, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
          <Typography variant="h6" gutterBottom>What Themes Control</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Background</Typography>
                <Typography variant="body2" color="text.secondary">Board and cell backgrounds</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Colors</Typography>
                <Typography variant="body2" color="text.secondary">Headers, text, accents</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Borders</Typography>
                <Typography variant="body2" color="text.secondary">Cell borders and styling</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Typography</Typography>
                <Typography variant="body2" color="text.secondary">Font styles and sizes</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Theme Store */}
      <Box id="theme-store" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <StoreIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Theme Store
        </Typography>

        <Typography variant="body1" paragraph>
          Browse the Theme Store to find pre-designed themes. Some themes are free, while premium themes can be purchased with credits.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Theme Categories</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Sports" secondary="NFL, NBA, NHL, MLB team themes" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Events" secondary="Super Bowl, March Madness, World Series" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Seasons" secondary="Holiday, summer, and seasonal designs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Classic" secondary="Clean, professional looks" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Store Features</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><PreviewIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Preview before buying" secondary="See how themes look on a sample board" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="One-time purchase" secondary="Use purchased themes on unlimited boards" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Instant access" secondary="Themes available immediately after purchase" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <VisualExample title="Theme Ownership" caption="Purchased themes are added to your theme library">
          <Grid container spacing={2} justifyContent="center">
            <Grid size="grow">
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50', border: 1, borderColor: 'success.main' }}>
                <Chip label="OWNED" color="success" size="small" sx={{ mb: 1 }} />
                <Typography variant="subtitle2">Super Bowl LVIII</Typography>
                <Typography variant="caption" color="text.secondary">Premium Theme</Typography>
              </Paper>
            </Grid>
            <Grid size="grow">
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50', border: 1, borderColor: 'success.main' }}>
                <Chip label="OWNED" color="success" size="small" sx={{ mb: 1 }} />
                <Typography variant="subtitle2">Classic Dark</Typography>
                <Typography variant="caption" color="text.secondary">Free Theme</Typography>
              </Paper>
            </Grid>
            <Grid size="grow">
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.100', border: 1, borderColor: 'grey.300' }}>
                <Chip label="$0.99" size="small" sx={{ mb: 1 }} />
                <Typography variant="subtitle2">March Madness</Typography>
                <Typography variant="caption" color="text.secondary">Premium Theme</Typography>
              </Paper>
            </Grid>
          </Grid>
        </VisualExample>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Applying Themes */}
      <Box id="applying-themes" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Applying Themes to Boards
        </Typography>

        <Typography variant="body1" paragraph>
          Apply themes when creating a board or change them later from board settings.
        </Typography>

        <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>When to Apply a Theme</Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                <Typography variant="subtitle2" color="primary.main" fontWeight="bold">During Creation</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a theme when setting up your new board
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                <Typography variant="subtitle2" color="primary.main" fontWeight="bold">From Board Settings</Typography>
                <Typography variant="body2" color="text.secondary">
                  Change theme anytime in Draft, Live, or Locked status
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', textAlign: 'center' }}>
                <Typography variant="subtitle2" color="primary.main" fontWeight="bold">Via Templates</Typography>
                <Typography variant="body2" color="text.secondary">
                  Include a theme in your board template
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="info" sx={{ mt: 3 }}>
          <strong>Tip:</strong> You can change your board's theme at any time—even after the board is locked. Theme changes are purely visual and don't affect game mechanics.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* FAQ */}
      <Box id="theme-faq" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Common Questions
        </Typography>

        <Grid container spacing={2}>
          {[
            {
              q: 'Can I use a purchased theme on multiple boards?',
              a: 'Yes! Once you purchase a theme, it\'s added to your library and can be used on unlimited boards.',
            },
            {
              q: 'Are themes visible to all players?',
              a: 'Yes, the theme you select is what all players see when viewing your board.',
            },
            {
              q: 'Can I preview a theme before buying?',
              a: 'Yes! Click on any theme in the store to see a full preview on a sample board before purchasing.',
            },
            {
              q: 'How do I apply a theme to my board?',
              a: 'Select a theme during board creation, or change it anytime from board settings. Theme changes are instant.',
            },
          ].map((item, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {item.q}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.a}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SupportLayout>
  );
};

export default ThemesSupportPage;
